import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Murray Road between Sydney Road and Gilbert Road
const FALLBACK_CENTER = { lat: -37.741, lng: 144.963 }; // Correct Murray Road center
const FALLBACK_ZOOM = 13.0; // Better zoom level for Murray Road section
const FALLBACK_RESTRICTIONS = {
  enabled: true,
  bounds: {
    north: -37.73,
    south: -37.74,
    east: 145.005,
    west: 144.96,
  },
  strictBounds: false,
  zoomLevel: 13,
};

// Global cache to prevent duplicate API calls
let configCache = null;
let configPromise = null;

export default function useDefaultMapConfig() {
  const [defaultCenter, setDefaultCenter] = useState(FALLBACK_CENTER);
  const [defaultZoom, setDefaultZoom] = useState(FALLBACK_ZOOM);
  const [mapRestrictions, setMapRestrictions] = useState(FALLBACK_RESTRICTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDefaults = useCallback(async () => {
    // Return cached data if available
    if (configCache) {
      console.log('🔍 useDefaultMapConfig: Using cached config');
      setDefaultCenter(configCache.defaultCenter);
      setDefaultZoom(configCache.defaultZoom);
      setMapRestrictions(configCache.mapRestrictions);
      setLoading(false);
      return;
    }

    // If there's already a request in progress, wait for it
    if (configPromise) {
      console.log('🔍 useDefaultMapConfig: Waiting for existing request');
      try {
        const config = await configPromise;
        setDefaultCenter(config.defaultCenter);
        setDefaultZoom(config.defaultZoom);
        setMapRestrictions(config.mapRestrictions);
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    // Create a new request promise
    configPromise = (async () => {
      try {
        const configRes = await api.getAppConfig();
        const config = configRes.config || {};
        console.log('🔍 useDefaultMapConfig: Loaded config:', config);

        const result = {
          defaultCenter: config.defaultMapCenter || FALLBACK_CENTER,
          defaultZoom: config.defaultMapZoom || FALLBACK_ZOOM,
          mapRestrictions: config.mapRestrictions || FALLBACK_RESTRICTIONS,
        };

        // Cache the result
        configCache = result;

        setDefaultCenter(result.defaultCenter);
        setDefaultZoom(result.defaultZoom);
        setMapRestrictions(result.mapRestrictions);

        return result;
      } catch (err) {
        console.error('🔍 useDefaultMapConfig: Error loading config:', err);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
        configPromise = null; // Clear the promise
      }
    })();

    try {
      await configPromise;
    } catch (err) {
      // Error already handled in the promise
    }
  }, []);

  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  return {
    defaultCenter,
    defaultZoom,
    mapRestrictions,
    loading,
    error,
    reloadDefaults: loadDefaults,
  };
}

// Function to clear cache (useful when config is updated)
export function clearConfigCache() {
  configCache = null;
  configPromise = null;
}
